import { useState, useEffect } from "preact/hooks";
import uPlot from "uplot";
import { formatBps, formatMs } from "../../utils/format";

export type ChartThemeColors = {
  grid: string;
  text: string;
  blue: string;
  purple: string;
  green: string;
  orange: string;
  red: string;
  bg: string;
};

export function getChartColors(): ChartThemeColors {
  const style = getComputedStyle(document.documentElement);
  const get = (name: string) => style.getPropertyValue(name).trim();

  return {
    grid: get("--border-color"),
    text: get("--text-muted"),
    blue: get("--accent-blue"),
    purple: get("--accent-purple"),
    green: get("--accent-green"),
    orange: get("--accent-orange"),
    red: get("--accent-red"),
    bg: get("--bg-card"),
  };
}

export function colorWithAlpha(color: string, alpha: number): string {
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }

  if (color.startsWith("rgba(")) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
  }

  return color;
}

export function isoToUnixSeconds(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 1000);
}

export function createTooltip(): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "uplot-tooltip";
  el.style.position = "absolute";
  el.style.display = "none";
  el.style.pointerEvents = "none";
  el.style.zIndex = "100";
  el.style.padding = "0.5rem 0.75rem";
  el.style.borderRadius = "0.5rem";
  el.style.fontSize = "0.75rem";
  el.style.background = "var(--bg-card)";
  el.style.color = "var(--text-primary)";
  el.style.border = "1px solid var(--border-color)";
  el.style.boxShadow = "var(--shadow)";
  return el;
}

export function makeTimeFormatter(timestamps: number[]) {
  const span =
    timestamps.length > 1
      ? timestamps[timestamps.length - 1] - timestamps[0]
      : 0;
  const under24h = span <= 86400;

  const fmt = new Intl.DateTimeFormat(
    "id-ID",
    under24h
      ? { hour: "2-digit", minute: "2-digit", hour12: false }
      : { day: "2-digit", month: "2-digit" }
  );

  return (_u: uPlot, splits: number[]) =>
    splits.map((v) => fmt.format(new Date(v * 1000)));
}

export function formatBpsAxis(_u: uPlot, splits: number[]): string[] {
  return splits.map((v) => formatBps(v));
}

export function formatMsAxis(_u: uPlot, splits: number[]): string[] {
  return splits.map((v) => formatMs(v));
}

export function useChartTheme(): string {
  const [theme, setTheme] = useState(
    document.documentElement.dataset.theme ?? "light"
  );

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          setTheme(document.documentElement.dataset.theme ?? "light");
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return theme;
}
